import { fmtDateTime } from '@/lib/dates.js';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Info, Loader2, ScrollText } from 'lucide-react';
import { adminAudit, adminSites, adminUsers } from '@/api/client';
import { humanAction, humanRole, humanResource, humanDetails } from '@/lib/humanise';
import { PageHeader } from '@/components/layout/PageHeader';
import { MultiSelectSearch, type MultiSelectOption } from '@/components/ui/MultiSelectSearch';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';


/**
 * DREAM-8 — Cross-site audit log (Platform Audit Log).
 *
 * Server-side paginated. Multi-select User + Action filters; site stays single
 * (one site at a time is the platform-admin workflow). User_id / action are
 * sent as comma-separated strings → backend csvList() turns them into SQL IN().
 */

const ACTION_OPTIONS: MultiSelectOption[] = [
  { code: 'auth.login.password.success', label: 'Password verified' },
  { code: 'auth.login.password.failure', label: 'Password failed' },
  { code: 'auth.login.complete', label: 'Logged in' },
  { code: 'auth.logout', label: 'Logged out' },
  { code: 'auth.token.refreshed', label: 'Session refreshed' },
  { code: 'auth.site_switched', label: 'Switched site' },
  { code: 'auth.mfa.enrolled', label: 'MFA enrolled' },
  { code: 'auth.mfa.bypassed', label: 'MFA bypassed (testing)' },
  { code: 'auth.password_reset.requested', label: 'Password reset requested' },
  { code: 'auth.password_reset.completed', label: 'Password reset completed' },
  { code: 'auth.password.locked', label: 'Account locked' },
  { code: 'auth.unlocked', label: 'Account unlocked' },
  { code: 'site.created', label: 'Site created' },
  { code: 'site.updated', label: 'Site updated' },
  { code: 'site.deleted', label: 'Site deleted' },
  { code: 'invite.sent', label: 'Invitation sent' },
  { code: 'invite.resent', label: 'Invitation resent' },
  { code: 'invite.activated', label: 'Invitation accepted' },
  { code: 'admin.user.created', label: 'User created' },
  { code: 'admin.user.deleted', label: 'User deleted' },
  { code: 'admin.user.locked', label: 'User locked' },
  { code: 'admin.user.unlocked', label: 'User unlocked' },
  { code: 'admin.user.role_changed', label: 'Role changed' },
  { code: 'phi.grant.created', label: 'PHI access granted' },
  { code: 'phi.grant.revoked', label: 'PHI access revoked' },
  { code: 'csr.job.created', label: 'CSR export requested' },
  { code: 'csr.job.downloaded', label: 'CSR export downloaded' },
].map((a) => ({ value: a.code, label: a.label, sublabel: a.code }));

export function AdminAuditPage() {
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;

  const sites = useQuery({ queryKey: ['admin-sites'], queryFn: () => adminSites.list() });
  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => adminUsers.list() });

  const sitesById = useMemo(() => {
    const m = new Map<string, string>();
    sites.data?.sites.forEach((s) => m.set(s.site_id, s.name));
    return m;
  }, [sites.data]);

  const usersById = useMemo(() => {
    const m = new Map<string, { full_name: string; email: string }>();
    users.data?.rows.forEach((u) => m.set(u.user_id, { full_name: u.full_name, email: u.email }));
    return m;
  }, [users.data]);

  const userOptions = useMemo<MultiSelectOption[]>(
    () =>
      (users.data?.rows ?? []).map((u) => ({
        value: u.user_id,
        label: u.full_name || u.email.split('@')[0] || u.email,
        sublabel: u.email,
      })),
    [users.data],
  );

  const filters = {
    site_id: selectedSite || undefined,
    user_id: selectedUserIds.length > 0 ? selectedUserIds.join(',') : undefined,
    action: selectedActions.length > 0 ? selectedActions.join(',') : undefined,
    from: from || undefined,
    to: to || undefined,
  };

  const audit = useQuery({
    queryKey: ['admin-audit', filters, page],
    queryFn: () => adminAudit.list({ ...filters, page, limit }),
  });

  const displayUser = (userId: string | null) => {
    if (!userId) return '—';
    const u = usersById.get(userId);
    if (u) return u.full_name || u.email;
    return '—';
  };

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="Platform audit log"
        subtitle="All sites. Every action across the platform, immutable. For one site's events, use Compliance → Site Audit Trail."
      />

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="label text-xs">Site</label>
            <select className="input" value={selectedSite}
              onChange={(e) => { setSelectedSite(e.target.value); setPage(1); }}>
              <option value="">All sites</option>
              {sites.data?.sites.map((s) => (
                <option key={s.site_id} value={s.site_id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">Action</label>
            <MultiSelectSearch
              options={ACTION_OPTIONS}
              value={selectedActions}
              onChange={(v) => { setSelectedActions(v); setPage(1); }}
              placeholder="Any action"
            />
          </div>
          <div>
            <label className="label text-xs">User</label>
            <MultiSelectSearch
              options={userOptions}
              value={selectedUserIds}
              onChange={(v) => { setSelectedUserIds(v); setPage(1); }}
              placeholder="Any user"
            />
          </div>
          <div>
            <label className="label text-xs">From</label>
            <input className="input" type="datetime-local"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </div>
          <div>
            <label className="label text-xs">To</label>
            <input className="input" type="datetime-local"
              value={to}
              onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {audit.isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {audit.isError && (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(audit.error)}`}>
            {isPermissionError(audit.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{errorText(audit.error, 'Failed to load audit')}</span>
          </div>
        )}
        {audit.data && audit.data.rows.length === 0 && (
          <div className="p-8 text-center text-sm text-ink-muted">
            <ScrollText className="mx-auto mb-2 h-10 w-10 opacity-50" />
            No audit events match the current filters.
          </div>
        )}
        {audit.data && audit.data.rows.length > 0 && (
          <>
            <table className="min-w-full text-sm">
              <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-3 py-2">Timestamp</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Site</th>
                  <th className="px-3 py-2">Resource</th>
                  <th className="px-3 py-2">Details</th>
                  <th className="px-3 py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.rows.map((r) => (
                  <tr key={r.audit_id} className="border-b border-surface-border last:border-0 align-top">
                    <td className="px-3 py-2 text-xs text-ink-muted whitespace-nowrap">
                      {fmtDateTime(r.timestamp)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="badge badge-info">{humanAction(r.action)}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium text-ink-primary">{displayUser(r.user_id)}</div>
                      {usersById.get(r.user_id ?? '')?.email && (
                        <div className="text-[10px] text-ink-muted">
                          {usersById.get(r.user_id ?? '')!.email}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {humanRole(r.user_role_at_time)}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.site_id ? (sitesById.get(r.site_id) ?? '—') : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {humanResource(r.resource_type, r.resource_id, { users: usersById, sites: sitesById })}
                    </td>
                    <td className="px-3 py-2 text-xs max-w-md">
                      {(() => {
                        const summary = humanDetails(
                          r.action,
                          r.new_value as unknown as Record<string, unknown> | null,
                          { users: usersById, sites: sitesById },
                        );
                        if (summary) {
                          return <div className="text-ink-secondary">{summary}</div>;
                        }
                        return <span className="text-ink-muted">—</span>;
                      })()}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">{r.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-surface-border p-3 text-xs text-ink-muted">
              <span>Page {audit.data.page} · {audit.data.rows.length} of {audit.data.total} rows</span>
              <div className="flex gap-2">
                <button type="button" className="btn-ghost text-xs"
                  disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="h-3 w-3" /> Prev
                </button>
                <button type="button" className="btn-ghost text-xs"
                  disabled={!audit.data.has_more} onClick={() => setPage((p) => p + 1)}>
                  Next <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
